import { API_BASE_URL } from '../config';

const INSPECTOR_BASE = `${API_BASE_URL}/inspector`;

// Helper de reintento automático para redes inestables en campo (4G / 3G)
export const fetchWithRetry = async (url, options = {}, retries = 2, delayMs = 1200, timeoutMs = 25000) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const fetchOptions = {
                ...options,
                signal: options.signal || controller.signal
            };
            const response = await fetch(url, fetchOptions);
            clearTimeout(timer);
            return response;
        } catch (err) {
            clearTimeout(timer);
            const isTimeout = err.name === 'AbortError';
            const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
            const isNetworkSocketError = err.message && (
                err.message.includes('Failed to fetch') || 
                err.message.includes('NetworkError') ||
                err.message.includes('Load failed')
            );
            const isRetryable = isTimeout || isOffline || isNetworkSocketError;

            if (isRetryable && attempt < retries) {
                console.warn(`📶 [FetchRetry] Intento ${attempt + 1} falló por red (${isTimeout ? `Timeout ${timeoutMs}ms` : 'Error socket'}). Reintentando en ${delayMs}ms... (${retries - attempt} restantes)`);
                await new Promise(res => setTimeout(res, delayMs));
                continue;
            }
            if (isTimeout) {
                throw new Error(`Tiempo de espera agotado (${Math.round(timeoutMs / 1000)}s) al transferir datos al servidor. La velocidad de subida móvil en esta zona es muy lenta.`);
            }
            throw err;
        }
    }
};

export const inspectorService = {
    // Obtener incidencias pendientes
    getPendingUnrealizables: async () => {
        const token = localStorage.getItem("control_campo_token");
        console.log('🔗 [Service] URL:', `${INSPECTOR_BASE}/pending-unrealizables`);
        console.log('🔑 [Service] Token disponible:', !!token);

        try {
            const response = await fetchWithRetry(`${INSPECTOR_BASE}/pending-unrealizables`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('📡 [Service] Response status:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ [Service] Error HTTP:', response.status, errorText);
                throw new Error(`Error ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            console.log('📊 [Service] Response data:', data);
            console.log('✅ [Service] Success:', data.success);
            console.log('🔢 [Service] Count:', data.count);
            console.log('📋 [Service] Unrealizables array:', data.unrealizables);
            console.log('📋 [Service] Unrealizables length:', data.unrealizables?.length);

            if (data.unrealizables && data.unrealizables.length > 0) {
                console.log('👤 [Service] Primer tecnico:', data.unrealizables[0].technician);
                console.log('🏷️ [Service] Primer motivo:', data.unrealizables[0].reason);
            }

            return data;
        } catch (error) {
            console.error('💥 [Service] Error en fetch:', error);
            throw error;
        }
    },

    // Obtener detalles de incidencia
    getUnrealizableDetail: async (id) => {
        const token = localStorage.getItem("control_campo_token");
        console.log('🔗 [Service] Obteniendo detalle de incidencia:', id);

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/unrealizable/${id}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Error obteniendo detalles:', response.status, errorText);
            throw new Error(`Error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('📊 [Service] Detalle obtenido:', data);
        return data;
    },

    // Aprobar incidencia
    approveUnrealizable: async (id, data) => {
        const token = localStorage.getItem("control_campo_token");
        console.log('✅ [Service] Aprobando incidencia:', id);
        console.log('📦 [Service] Data:', data);

        const formData = new FormData();

        formData.append('inspector_lat', data.latitude || data.inspector_lat);
        formData.append('inspector_lng', data.longitude || data.inspector_lng);
        formData.append('justification_duration', data.justification_duration.toString());

        if (data.inspector_comments) {
            formData.append('inspector_comments', data.inspector_comments);
        }

        // Agregar fotos de evidencia
        data.evidence_photos.forEach((photo) => {
            formData.append('evidence_photos', photo);
        });

        console.log('📤 [Service] FormData preparado, enviando...');

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/unrealizable/${id}/approve`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('❌ Error aprobando:', response.status, errorData);
            throw new Error(errorData.detail || `Error ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ [Service] Aprobación exitosa:', result);
        return result;
    },

    // Rechazar incidencia
    rejectUnrealizable: async (id, data) => {
        const token = localStorage.getItem("control_campo_token");
        console.log('❌ [Service] Rechazando incidencia:', id);
        console.log('📦 [Service] Data:', data);

        const formData = new FormData();

        formData.append('inspector_lat', data.latitude || data.inspector_lat);
        formData.append('inspector_lng', data.longitude || data.inspector_lng);
        formData.append('inspector_comments', data.inspector_comments);

        // Agregar fotos de evidencia
        data.evidence_photos.forEach((photo) => {
            formData.append('evidence_photos', photo);
        });

        console.log('📤 [Service] FormData preparado, enviando...');

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/unrealizable/${id}/reject`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('❌ Error rechazando:', response.status, errorData);
            throw new Error(errorData.detail || `Error ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ [Service] Rechazo exitoso:', result);
        return result;
    },

    // Obtener historial
    getMyApprovals: async () => {
        const token = localStorage.getItem("control_campo_token");
        console.log('📚 [Service] Obteniendo historial...');

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/my-approvals`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Error obteniendo historial:', response.status, errorText);
            throw new Error(`Error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('📊 [Service] Historial obtenido:', data);
        return data;
    },

    // Obtener dashboard (Recorridos)
    getInspectorDashboard: async (params) => {
        const token = localStorage.getItem("control_campo_token");
        const queryParams = new URLSearchParams(params || {}).toString();
        const url = `${INSPECTOR_BASE}/dashboard?${queryParams}`;
        console.log('🔗 [Service] Obteniendo dashboard:', url);

        const response = await fetchWithRetry(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Error obteniendo dashboard:', response.status, errorText);
            throw new Error(`Error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        return data;
    },

    // Auditar un recorrido (Nuevo formulario del Inspector)
    auditRoute: async (reportId, data) => {
        const token = localStorage.getItem("control_campo_token");
        console.log(`✅ [Service] Auditando recorrido: ${reportId}`);

        const formData = new FormData();
        formData.append('distrito', data.distrito);
        formData.append('cluster', data.cluster || '');
        formData.append('orden_trabajo', data.orden_trabajo);
        formData.append('es_realizable', data.es_realizable ? 'true' : 'false');
        formData.append('latitude', data.latitude);
        formData.append('longitude', data.longitude);
        formData.append('poliza', data.poliza || 'PE');

        if (data.evidence_photos) {
            data.evidence_photos.forEach((photo) => {
                formData.append('evidence_photos', photo);
            });
        }

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/route/${reportId}/audit`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Error ${response.status}`);
        }

        return await response.json();
    },

    // NUEVO: Cargar datos del mapa (LAZY LOADING - solo cuando usuario abre el mapa)
    getRouteMapData: async (routeId) => {
        const token = localStorage.getItem("control_campo_token");
        const url = `${INSPECTOR_BASE}/routes/${routeId}/map-data`;
        console.log('🔗 [Service] Cargando datos del mapa para ruta:', routeId);

        const response = await fetchWithRetry(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Error cargando datos del mapa:', response.status, errorText);
            throw new Error(`Error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('✅ Datos del mapa cargados para ruta:', routeId);
        return data;
    },

    // ========== JORNADA / WORKDAY ==========

    // Iniciar jornada del inspector
    startWorkday: async (startLat, startLng, simulatedTime = null) => {
        const token = localStorage.getItem("control_campo_token");
        const formData = new FormData();
        formData.append('start_lat', startLat.toString());
        formData.append('start_lng', startLng.toString());
        if (simulatedTime) {
            formData.append('simulated_time', simulatedTime);
        }

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/workday/start`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Error ${response.status}`);
        }
        return await response.json();
    },

    // Registrar punto PE / PI
    registerPESegment: async (workdayId, latitude, longitude, networkType, incidences, photo, simulatedTime = null) => {
        const token = localStorage.getItem("control_campo_token") || localStorage.getItem("auth_token");
        if (!token) {
            throw new Error("No hay una sesión activa en el teléfono. Por favor vuelve a iniciar sesión.");
        }

        const formData = new FormData();
        formData.append('latitude', latitude.toString());
        formData.append('longitude', longitude.toString());
        if (networkType) formData.append('network_type', networkType);
        if (incidences) formData.append('incidences', JSON.stringify(incidences));

        // Validación y empaquetado seguro de la foto
        if (photo && photo instanceof Blob && photo.size > 0) {
            const fname = (photo instanceof File && photo.name) ? photo.name : 'pe_segment.jpg';
            formData.append('photo', photo, fname);
        } else if (typeof photo === 'string' && photo.startsWith('data:')) {
            try {
                const parts = photo.split(',');
                const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
                const bstr = atob(parts[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                const blob = new Blob([u8arr], { type: mime });
                formData.append('photo', blob, 'pe_segment.jpg');
            } catch (pErr) {
                console.warn('[registerPESegment] Error decodificando data URL:', pErr);
            }
        } else {
            // Si la foto en IndexedDB se corrompió o no es un Blob válido (evita que el punto quede atorado)
            console.warn("⚠️ [registerPESegment] Foto ausente o dañada en caché local. Creando evidencia placeholder.");
            try {
                const canvas = document.createElement('canvas');
                canvas.width = 400;
                canvas.height = 300;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#0f172a';
                ctx.fillRect(0, 0, 400, 300);
                ctx.fillStyle = '#f59e0b';
                ctx.font = 'bold 16px sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('📷 Evidencia Offline Recuperada', 200, 130);
                ctx.fillStyle = '#cbd5e1';
                ctx.font = '12px sans-serif';
                ctx.fillText('Punto registrado en campo sin imagen disponible', 200, 160);
                ctx.fillText(new Date().toLocaleString('es-MX'), 200, 185);

                const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                const parts = dataUrl.split(',');
                const bstr = atob(parts[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                const fallbackBlob = new Blob([u8arr], { type: 'image/jpeg' });
                formData.append('photo', fallbackBlob, 'pe_segment_recuperado.jpg');
            } catch (cErr) {
                console.error("Error creando fallback blob:", cErr);
            }
        }

        if (simulatedTime) {
            formData.append('simulated_time', simulatedTime);
        }

        const response = await fetchWithRetry(
            `${INSPECTOR_BASE}/workday/${workdayId}/pe-segment`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            },
            2,     // 2 reintentos
            1500,  // 1.5s entre reintentos
            45000  // 45 segundos de timeout para fotos en redes móviles
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            let detailMsg = errorData.detail;
            if (Array.isArray(detailMsg)) {
                detailMsg = detailMsg.map(d => d.msg || JSON.stringify(d)).join(', ');
            }
            throw new Error(detailMsg || `Error ${response.status}`);
        }
        return await response.json();
    },

    // Finalizar jornada
    endWorkday: async (workdayId, endLat, endLng, simulatedTime = null) => {
        const token = localStorage.getItem("control_campo_token");
        const formData = new FormData();
        formData.append('end_lat', endLat.toString());
        formData.append('end_lng', endLng.toString());
        if (simulatedTime) {
            formData.append('simulated_time', simulatedTime);
        }

        const response = await fetchWithRetry(`${INSPECTOR_BASE}/workday/${workdayId}/end`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Error ${response.status}`);
        }
        return await response.json();
    },

    // Obtener jornada activa (para recuperar estado)
    getActiveWorkday: async () => {
        const token = localStorage.getItem("control_campo_token");
        const response = await fetchWithRetry(`${INSPECTOR_BASE}/workday/active`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error ${response.status}: ${errorText}`);
        }
        return await response.json();
    }
};